<template>
  <div>
    <span class="caption font-weight-medium" v-if="translatedLabel">
      {{ translatedLabel }} <span class="error--text" v-if="required">*</span>
    </span>
    <v-menu
      ref="menu"
      class="rounded-lg"
      v-model="menu"
      style="overflow: hidden"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          :value="textFieldValue"
          prepend-inner-icon="mdi-calendar"
          readonly
          :hide-details="hideDetails"
          :rules="rules"
          :disabled="disabled"
          dense
          class="custom-field"
          solo
          flat
          filled
          :readonly="readonly"
          :class="[color === 'white' ? 'custom-field-white' : 'custom-field']"
          v-bind="attrs"
          v-on="on"
        ></v-text-field>
      </template>
      <v-card class="rounded-lg custom-shadow pa-0 ma-0" flat>
        <div class="d-flex">
          <v-date-picker
            v-model="selectedDate"
            class="rounded-lg"
            :range="!singleDate"
            color="primary"
            :readonly="readonly"
            no-title
            :min="min"
            :max="max"
            :locale="locale"
            :allowed-dates="allowedDates"
            scrollable
            :width="singleDate ? 290 : undefined"
          />
          <v-time-picker
            v-if="singleDate && selectedDate"
            v-model="selectedTime"
            format="24hr"
            color="primary"
            scrollable
            :readonly="readonly"
            class="ml-2 rounded-lg"
            width="290"
            ref="timePicker"
          />
        </div>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelSelection">
            {{ $t("cancel") }}
          </v-btn>
          <v-btn color="success" class="custom-shadow rounded-lg" @click="saveSelection">
            {{ $t("save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import hasModel from "@/mixins/hasModel";

export default {
  mixins: [hasModel],
  props: {
    label: {
      type: String,
      required: false,
      default: "",
    },
    disallowedDates: {
      type: Array,
      required: false,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: true,
    },
    singleDate: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    min: {
      type: [String, Boolean],
      required: false,
      default: undefined,
    },
    max: {
      type: [String, Boolean],
      required: false,
      default: undefined,
    },
    rules: {
      type: Array,
      required: false,
      default: () => [],
    },
    color: {
      type: String,
      required: false,
      default: "default",
    },
  },
  data() {
    return {
      menu: false,
      selectedDate: this.singleDate ? null : [], // For v-date-picker
      selectedTime: "00:00", // For v-time-picker (HH:mm format)
    };
  },
  watch: {
    menu(isOpen) {
      if (isOpen) {
        if (this.singleDate) {
          if (this.model && typeof this.model === "string") {
            const d = new Date(this.model);
            if (!isNaN(d.valueOf())) {
              this.selectedDate = d.toISOString().slice(0, 10);
              this.selectedTime = `${String(d.getHours()).padStart(2, "0")}:${String(
                d.getMinutes()
              ).padStart(2, "0")}`;
            } else {
              this.selectedDate = null;
              this.selectedTime = "00:00";
            }
          } else {
            this.selectedDate = null;
            this.selectedTime = "00:00";
          }
        } else {
          // Range mode
          this.selectedDate = Array.isArray(this.model) ? [...this.model] : [];
        }
      }
    },
    model: {
      handler(newValue) {
        if (!this.menu) {
          // Only sync if menu is not open
          if (this.singleDate) {
            if (newValue && typeof newValue === "string") {
              const d = new Date(newValue);
              if (!isNaN(d.valueOf())) {
                this.selectedDate = d.toISOString().slice(0, 10);
                this.selectedTime = `${String(d.getHours()).padStart(2, "0")}:${String(
                  d.getMinutes()
                ).padStart(2, "0")}`;
              } else {
                this.selectedDate = null;
                this.selectedTime = "00:00";
              }
            } else {
              this.selectedDate = null;
              this.selectedTime = "00:00";
            }
          } else {
            this.selectedDate = Array.isArray(newValue) ? [...newValue] : [];
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    allowedDates(val) {
      return !this.disallowedDates.includes(val);
    },
    saveSelection() {
      if (this.singleDate) {
        if (this.selectedDate && this.selectedTime) {
          const [hours, minutes] = this.selectedTime.split(":");
          const dateObj = new Date(this.selectedDate);
          dateObj.setHours(parseInt(hours, 10));
          dateObj.setMinutes(parseInt(minutes, 10));
          dateObj.setSeconds(0);
          dateObj.setMilliseconds(0);
          this.$emit("input", dateObj.toISOString());
        } else if (this.selectedDate && !this.selectedTime) {
          // Default time to 00:00 if only date is selected
          const dateObj = new Date(this.selectedDate);
          dateObj.setHours(0);
          dateObj.setMinutes(0);
          dateObj.setSeconds(0);
          dateObj.setMilliseconds(0);
          this.$emit("input", dateObj.toISOString());
        } else {
          this.$emit("input", null); // No date selected
        }
      } else {
        // Range mode
        if (Array.isArray(this.selectedDate) && this.selectedDate.length === 2) {
          this.selectedDate.sort();
        }
        this.$emit("input", this.selectedDate);
      }
      this.menu = false;
    },
    cancelSelection() {
      this.menu = false;
    },
    localeDateTime(
      isoString,
      options = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }
    ) {
      if (!isoString) return "";
      const date = new Date(isoString);
      if (isNaN(date.valueOf())) return "";
      return date.toLocaleDateString(this.locale, options);
    },
  },
  computed: {
    locale() {
      let locale = "";
      switch (this.$i18n.locale) {
        case "en":
          locale = "en-GB";
          break;
        case "hr":
          locale = "sr-RS";
          break;
        case "rs-lat":
          locale = "sr-RS";
          break;
        default:
          locale = "sr-RS";
      }
      return locale;
    },
    translatedLabel() {
      if (!this.label) return this.$t("date");
      return this.label;
    },
    textFieldValue() {
      if (this.singleDate) {
        return this.model ? this.localeDateTime(this.model) : "";
      } else {
        return Array.isArray(this.model) && this.model.length > 0
          ? this.model.map((date) => this.localeDate(date)).join(" - ")
          : "";
      }
    },
  },
};
</script>
